import { useQuery } from '@tanstack/react-query';
import { Loader2, Calendar, Hash, DollarSign, User, MapPin, BookOpen, Scan, FileSearch, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ClientDocument, ExtractedEntities, EmbeddingChunk } from '@/types/client';
import { formatFileSize, DOCUMENT_CATEGORY_LABELS } from '@/types/client';
import { clientsService } from '@/services/api/clients';

interface FileMetadataDialogProps {
  document: ClientDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FileMetadataDialog({ document, open, onOpenChange }: FileMetadataDialogProps) {
  const { data: metadata, isLoading } = useQuery({
    queryKey: ['file-metadata', document?.id],
    queryFn: () => clientsService.getFileMetadata(document!.id),
    enabled: open && !!document?.id,
    staleTime: 60_000,
  });

  if (!document) return null;

  const entities: ExtractedEntities | null = metadata?.file?.extractedEntities || document.extractedEntities || null;
  const chunks: EmbeddingChunk[] = metadata?.chunks || [];
  const fullAiSummary = metadata?.file?.aiSummary || document.aiSummary;
  const fullExtractedText = metadata?.file?.extractedText || document.extractedText;
  const docDate = metadata?.file?.documentDate || document.documentDate;
  const isScanned = metadata?.file?.isScanned ?? document.isScanned;
  const pageCount = metadata?.file?.pageCount ?? document.pageCount;
  const processingStatus = metadata?.file?.processingStatus;

  const hasOverviewData = fullAiSummary || document.description || document.tags?.length || docDate || pageCount;
  const hasExtractedData = entities && Object.keys(entities).some(k => {
    const val = (entities as any)[k];
    return val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);
  });
  const hasIndexedContent = chunks.length > 0 || fullExtractedText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 !flex flex-col overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="text-base font-semibold">{document.originalFilename}</DialogTitle>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {document.documentCategory && (
              <Badge variant="outline">
                {DOCUMENT_CATEGORY_LABELS[document.documentCategory] || document.documentCategory}
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              {document.fileType.toUpperCase()} - {formatFileSize(document.fileSizeBytes)}
            </Badge>
            {document.financialYear && (
              <Badge variant="outline" className="text-xs">FY{document.financialYear}</Badge>
            )}
            {isScanned && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                <Scan className="h-3 w-3 mr-1" />
                Scanned
              </Badge>
            )}
            {pageCount && (
              <Badge variant="secondary" className="text-xs">
                {pageCount} page{pageCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {processingStatus && (
              <Badge variant={processingStatus === 'completed' ? 'default' : 'secondary'} className="text-xs">
                {processingStatus}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <TabsList className="justify-start px-6 bg-transparent border-b rounded-none h-10 shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="extracted" disabled={!hasExtractedData}>
                Extracted Data
              </TabsTrigger>
              <TabsTrigger value="indexed" disabled={!hasIndexedContent}>
                Indexed Content {chunks.length > 0 && `(${chunks.length})`}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 overflow-auto">
              {/* Overview Tab */}
              <TabsContent value="overview" className="px-6 py-4 mt-0 space-y-4">
                {fullAiSummary && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">AI Summary</p>
                    <p className="text-sm leading-relaxed">{fullAiSummary}</p>
                  </div>
                )}
                {document.description && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                    <p className="text-sm">{document.description}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {docDate && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Date:</span>
                      <span>{docDate}</span>
                    </div>
                  )}
                  {entities?.documentYear && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Year:</span>
                      <span>{entities.documentYear}</span>
                    </div>
                  )}
                  {entities?.period && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Period:</span>
                      <span>{entities.period}</span>
                    </div>
                  )}
                </div>
                {document.tags && document.tags.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {document.tags.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {entities?.keyContent && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Key Content</p>
                    <p className="text-sm leading-relaxed">{entities.keyContent}</p>
                  </div>
                )}
                {!hasOverviewData && !entities?.keyContent && (
                  <p className="text-sm text-muted-foreground py-4">
                    No metadata available. Run "Index & Analyze" to extract metadata from this file.
                  </p>
                )}
              </TabsContent>

              {/* Extracted Data Tab */}
              <TabsContent value="extracted" className="px-6 py-4 mt-0 space-y-4">
                {entities ? (
                  <>
                    {(entities.clientName || entities.abn || entities.acn || entities.tfn) && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Identifiers</p>
                        <div className="grid grid-cols-2 gap-2">
                          {entities.clientName && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">Name:</span>
                              <span className="font-medium truncate">{entities.clientName}</span>
                            </div>
                          )}
                          {entities.abn && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">ABN:</span>
                              <span className="font-medium">{entities.abn}</span>
                            </div>
                          )}
                          {entities.acn && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">ACN:</span>
                              <span className="font-medium">{entities.acn}</span>
                            </div>
                          )}
                          {entities.tfn && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">TFN:</span>
                              <span className="font-medium">{entities.tfn}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {entities.amounts && entities.amounts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Amounts</p>
                        <div className="space-y-1">
                          {entities.amounts.map((amt, i) => (
                            <div key={i} className="flex items-center justify-between text-sm p-2 rounded border bg-muted/30">
                              <div className="flex items-center gap-1.5">
                                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{amt.label}</span>
                              </div>
                              <span className="font-medium">${amt.value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                            </div>
                          ))}
                          {entities.totalAmount != null && (
                            <div className="flex items-center justify-between text-sm p-2 rounded border bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                              <span className="font-medium">Total</span>
                              <span className="font-bold">${entities.totalAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {entities.referenceNumbers && entities.referenceNumbers.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Reference Numbers</p>
                        <div className="flex flex-wrap gap-2">
                          {entities.referenceNumbers.map((ref, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {ref.type}: {ref.number}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {entities.keyDates && entities.keyDates.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Key Dates</p>
                        <div className="space-y-1">
                          {entities.keyDates.map((kd, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-muted-foreground">{kd.label}:</span>
                              <span className="font-medium">{kd.date}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(entities.payee || entities.payer || entities.address) && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Parties</p>
                        <div className="grid grid-cols-2 gap-2">
                          {entities.payee && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">Payee:</span>
                              <span className="font-medium truncate">{entities.payee}</span>
                            </div>
                          )}
                          {entities.payer && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30">
                              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">Payer:</span>
                              <span className="font-medium truncate">{entities.payer}</span>
                            </div>
                          )}
                          {entities.address && (
                            <div className="flex items-center gap-1.5 text-sm p-2 rounded border bg-muted/30 col-span-2">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">Address:</span>
                              <span className="font-medium truncate">{entities.address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {entities.names && entities.names.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Names Found</p>
                        <div className="flex flex-wrap gap-1">
                          {entities.names.map((name, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{name}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {entities.accountNumbers && entities.accountNumbers.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Account Numbers</p>
                        <div className="flex flex-wrap gap-1">
                          {entities.accountNumbers.map((acct, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-mono">{acct}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">No extracted data available.</p>
                )}
              </TabsContent>

              {/* Indexed Content Tab */}
              <TabsContent value="indexed" className="px-6 py-4 mt-0 space-y-4">
                {chunks.length > 0 ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileSearch className="h-4 w-4" />
                      <span>{chunks.length} text chunk{chunks.length !== 1 ? 's' : ''} indexed for AI search</span>
                    </div>
                    <div className="space-y-2">
                      {chunks.map((chunk) => (
                        <div key={chunk.chunkIndex} className="p-3 rounded border bg-muted/30">
                          <div className="flex items-center justify-between mb-1.5">
                            <Badge variant="secondary" className="text-xs">Chunk {chunk.chunkIndex + 1}</Badge>
                          </div>
                          <p className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-muted-foreground">
                            {chunk.chunkText}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : fullExtractedText ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BookOpen className="h-4 w-4" />
                      <span>Extracted text (not yet vectorized)</span>
                    </div>
                    <div className="p-3 rounded border bg-muted/30">
                      <p className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-muted-foreground">
                        {fullExtractedText}
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">No indexed content available.</p>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
